import { useCallback, useMemo, useRef, useState } from 'react'
import { useToast } from '@chakra-ui/react'
import { updateDoc } from 'firebase/firestore'
import FirestoreUser, {
  Profile,
  UnitSystem,
} from '../../../../../types/FirestoreUser'
import userDoc from '../../../../../utils/userDoc'
import useHandleError from '../../../../../hooks/useHandleError'

interface UsePersonalFormProps {
  uid: string
  firestoreUser: FirestoreUser
  isImpersonating: boolean
  canEditLockedMedicalDates?: boolean
}

export const usePersonalForm = ({
  uid,
  firestoreUser,
  canEditLockedMedicalDates = false,
}: UsePersonalFormProps) => {
  const [profile, setProfile] = useState<Profile>(
    firestoreUser.profile || ({} as Profile),
  )
  const [isSaved, setIsSaved] = useState(false)
  const [loading, setLoading] = useState(false)

  const units = firestoreUser.unitSystem || 'imperial'
  const prevDiagnosisRef = useRef(profile.diagnosis)

  const handleError = useHandleError()
  const toast = useToast()

  const extraInfoRequired = useMemo(() => {
    return (
      profile.diagnosis === 'als' ||
      profile.diagnosis === 'pls' ||
      profile.diagnosis === 'other'
    )
  }, [profile.diagnosis])

  const areDateFieldsDisabled = useMemo(() => {
    return !extraInfoRequired || profile.diagnosis === 'administrator'
  }, [extraInfoRequired, profile.diagnosis])

  const areFirstSymptomsFieldsDisabled = useMemo(() => {
    return (
      profile.diagnosis === 'administrator' ||
      profile.diagnosis === 'medical' ||
      profile.diagnosis === 'nonprofit'
    )
  }, [profile.diagnosis])

  // Lock flags — based on initial Firestore data, not local form state
  const isBirthDateLocked = !!firestoreUser.profile?.birthDate
  const isDiagnosisDateLocked =
    !canEditLockedMedicalDates && !!firestoreUser.profile?.diagnosisDate
  const isFirstSymptomsDateLocked =
    !canEditLockedMedicalDates && !!firestoreUser.profile?.firstSymptomsDate

  const update = useCallback((changes: Partial<Profile>) => {
    setProfile((current) => ({ ...current, ...changes }))
    setIsSaved(false)
  }, [])

  const updateUnitSystem = useCallback(
    async (unitSystem: UnitSystem) => {
      if (!uid) return

      await updateDoc(userDoc(uid), {
        unitSystem,
      })
    },
    [uid],
  )

  return {
    profile,
    setProfile,
    isSaved,
    setIsSaved,
    loading,
    setLoading,
    units,
    prevDiagnosisRef,
    extraInfoRequired,
    areDateFieldsDisabled,
    areFirstSymptomsFieldsDisabled,
    update,
    updateUnitSystem,
    handleError,
    toast,
    isBirthDateLocked,
    isDiagnosisDateLocked,
    isFirstSymptomsDateLocked,
  }
}
